import {
  Box, Button, Chip, Typography, Select, MenuItem,
  FormControl, Container, Pagination, Badge,
} from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import Snackbar from "@mui/material/Snackbar";
import { CircularProgress } from "@mui/material";
import { connect } from "react-redux";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import CardListing from "../../components/listing/CardListing";
import * as Actions from "../../redux/actions";
import HomeCategories from "../../components/home/HomeCategories";
import IconButton from "@mui/material/IconButton";
import TuneIcon from "@mui/icons-material/Tune";
import FilterModal from "./FilterModal";
import ListView from "./ListView";
import PetsIcon from "@mui/icons-material/Pets";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import { Alert } from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import ListIcon from "@mui/icons-material/List";
import SortIcon from "@mui/icons-material/Sort";
import { Helmet } from "react-helmet-async";

const ProductListing = ({
  classes,
  productListings,
  loading,
  loadListings,
  totalListing,
  jwtToken,
  error,
}) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage] = useState(25);
  const [open, setOpen] = useState(false);
  const [filters, setFilter] = useState([]);
  const [view, setView] = useState("Grid");
  const [sortBy, setSortBy] = useState("newest");
  const [snackbarStates, setSnackbarStates] = useState({ open: false, message: "", severity: "info" });

  const queryParams = new URLSearchParams(location.search);
  const isFeatured = queryParams.get("is_featured");
  const searchQuery = queryParams.get("search");

  const isServicesTab = location.pathname.includes("services");

  useEffect(() => {
    if (error && error !== "") {
      setSnackbarStates({ open: true, message: error, severity: "error" });
    }
  }, [error]);

  useEffect(() => {
    loadListings({
      pageNumber,
      rowsPerPage,
      breedId: params.breedId,
      categoryId: params.categoryId,
      sellerId: params.sellerId,
      subbreedId: params.subBreedId,
      varietyId: params.varietyId,
      geneticId: params.geneticsId,
      isFeatured,
      jwtToken: jwtToken || null,
      searchQuery: searchQuery || null,
    });
  }, [pageNumber, rowsPerPage, params.breedId, params.sellerId, params.categoryId, params.varietyId, loadListings, jwtToken, isFeatured, searchQuery]);

  const sortedListings = useMemo(() => {
    if (!productListings) return [];
    const sorted = [...productListings];
    switch (sortBy) {
      case "price_low": return sorted.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
      case "price_high": return sorted.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
      case "name_az": return sorted.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      default: return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
  }, [productListings, sortBy]);

  const totalPages = Math.ceil((totalListing || 0) / rowsPerPage);
  const activeFilterCount = filters.length + (searchQuery ? 1 : 0);

  const handleRemoveFilter = (indexToRemove) => {
    const remaining = filters.filter((_, i) => i !== indexToRemove);
    setFilter(remaining);
    const filtersData = { listing: {} };
    remaining.forEach((f) => { filtersData.listing[f.key] = f.value; });
    loadListings({ filtersData, jwtToken });
  };

  return (
    <>
      <Helmet>
        <meta property="og:image" content="https://prodappdata.s3.amazonaws.com/preview-images/animal-listings-preview.png" />
        <meta property="og:title" content="Animal Listings" />
        <meta property="og:description" content="Moore Exotics Animal Listings" />
      </Helmet>

      <Snackbar open={snackbarStates.open} autoHideDuration={3000} onClose={() => setSnackbarStates({ ...snackbarStates, open: false })} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert variant="filled" severity={snackbarStates.severity} sx={{ width: "100%" }}>{snackbarStates.message}</Alert>
      </Snackbar>

      <FilterModal open={open} setOpen={setOpen} setFilter={setFilter} />

      <Box sx={{ minHeight: "100vh", backgroundColor: "#111" }}>

        {/* ── Hero header ── */}
        <Box sx={{ position: "relative", background: "linear-gradient(to bottom, #1a1a1a 0%, #111 100%)", borderBottom: "1px solid #222", py: { xs: 4, md: 5 }, overflow: "hidden" }}>
          <Box sx={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 0%, rgba(255,192,1,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />
          <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
            {/* Breadcrumb */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Typography component={Link} to="/" sx={{ color: "#777", fontSize: "13px", textDecoration: "none", "&:hover": { color: "#FFC001" } }}>Home</Typography>
              <Typography sx={{ color: "#555", fontSize: "13px" }}>/</Typography>
              <Typography sx={{ color: "#FFC001", fontSize: "13px", fontWeight: 600 }}>Listings</Typography>
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: "26px", md: "36px" }, color: "#fff", lineHeight: 1.1 }}>
              Browse <Box component="span" sx={{ color: "#FFC001" }}>Listings</Box>
            </Typography>
            <Typography sx={{ color: "#777", fontSize: "13px", mt: 0.5 }}>
              {totalListing ? `${totalListing.toLocaleString()} listings available` : "Browse all available listings"}
            </Typography>
          </Container>
        </Box>

        {/* ── Animals / Services sticky tab bar ── */}
        <Box sx={{ position: "sticky", top: 0, zIndex: 100, backgroundColor: "#0d0d0d", borderBottom: "1px solid #1e1e1e" }}>
          <Container maxWidth="xl">
            <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
              <Box
                onClick={() => navigate("/listings")}
                sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  px: 3, py: 1.6, cursor: "pointer",
                  borderBottom: !isServicesTab ? "2px solid #FFC001" : "2px solid transparent",
                  transition: "all 0.2s ease",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.03)" },
                }}
              >
                <PetsIcon sx={{ fontSize: 17, color: !isServicesTab ? "#FFC001" : "#666" }} />
                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: !isServicesTab ? "#FFC001" : "#666" }}>
                  Animals
                </Typography>
              </Box>
              <Box
                onClick={() => navigate("/services/listings")}
                sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  px: 3, py: 1.6, cursor: "pointer",
                  borderBottom: isServicesTab ? "2px solid #FFC001" : "2px solid transparent",
                  transition: "all 0.2s ease",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.03)" },
                }}
              >
                <MiscellaneousServicesIcon sx={{ fontSize: 17, color: isServicesTab ? "#FFC001" : "#666" }} />
                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: isServicesTab ? "#FFC001" : "#666" }}>
                  Services
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* ── Category carousel ── */}
        <Box sx={{ backgroundColor: "#111", borderBottom: "1px solid #1a1a1a" }}>
          <Container maxWidth="xl" sx={{ pt: 2, pb: 0.5 }}>
            <Typography sx={{ color: "#555", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", mb: 1 }}>
              Browse by Category
            </Typography>
          </Container>
          <HomeCategories classes={classes} page="Listing" />
        </Box>

        {/* ── Toolbar ── */}
        <Box sx={{ backgroundColor: "#0d0d0d", borderBottom: "1px solid #1a1a1a", py: 1.2 }}>
          <Container maxWidth="xl">
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>

              {/* Left: result count + active filter chips */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {totalListing > 0 && (
                  <Typography sx={{ color: "#555", fontSize: "13px", fontWeight: 600 }}>
                    {sortedListings.length} shown
                  </Typography>
                )}
                {searchQuery && (
                  <Chip
                    label={`"${searchQuery}"`}
                    onDelete={() => navigate("/listings")}
                    size="small"
                    sx={{ backgroundColor: "#FFC001", color: "#000", fontWeight: 700, fontSize: "12px", height: 24, "& .MuiChip-deleteIcon": { color: "#000", fontSize: 14 } }}
                  />
                )}
                {filters.map((f, i) => (
                  <Chip key={i} label={f.label || f} size="small"
                    onDelete={() => handleRemoveFilter(i)}
                    sx={{ backgroundColor: "rgba(255,192,1,0.12)", color: "#FFC001", border: "1px solid rgba(255,192,1,0.3)", fontSize: "12px", height: 24, "& .MuiChip-deleteIcon": { color: "#FFC001", fontSize: 14, "&:hover": { color: "#fff" } } }} />
                ))}
              </Box>

              {/* Right: sort + view + filter */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* Sort */}
                <FormControl size="small">
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    displayEmpty
                    startAdornment={<SortIcon sx={{ color: "#888", mr: 0.5, fontSize: 15 }} />}
                    sx={{
                      color: "#ccc", fontSize: "13px", height: "34px", minWidth: 155,
                      backgroundColor: "#1a1a1a",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#2a2a2a" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#FFC001" },
                      "& .MuiSvgIcon-root": { color: "#888" },
                    }}
                  >
                    <MenuItem value="newest">Newest First</MenuItem>
                    <MenuItem value="price_low">Price: Low → High</MenuItem>
                    <MenuItem value="price_high">Price: High → Low</MenuItem>
                    <MenuItem value="name_az">Name: A – Z</MenuItem>
                  </Select>
                </FormControl>

                {/* View toggle */}
                <Box sx={{ display: "flex", backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "8px", overflow: "hidden" }}>
                  <IconButton onClick={() => setView("Grid")} size="small" sx={{ borderRadius: 0, px: 1, backgroundColor: view === "Grid" ? "#FFC001" : "transparent", "&:hover": { backgroundColor: view === "Grid" ? "#e6ac00" : "#222" } }}>
                    <GridViewIcon sx={{ color: view === "Grid" ? "#000" : "#777", fontSize: 17 }} />
                  </IconButton>
                  <IconButton onClick={() => setView("List")} size="small" sx={{ borderRadius: 0, px: 1, backgroundColor: view === "List" ? "#FFC001" : "transparent", "&:hover": { backgroundColor: view === "List" ? "#e6ac00" : "#222" } }}>
                    <ListIcon sx={{ color: view === "List" ? "#000" : "#777", fontSize: 17 }} />
                  </IconButton>
                </Box>

                {/* Filter button with active badge */}
                <Badge badgeContent={activeFilterCount} color="warning" sx={{ "& .MuiBadge-badge": { backgroundColor: "#FFC001", color: "#000", fontWeight: 700, fontSize: "10px" } }}>
                  <Button
                    variant={activeFilterCount > 0 ? "contained" : "outlined"}
                    startIcon={<TuneIcon sx={{ fontSize: "16px !important" }} />}
                    onClick={() => setOpen(true)}
                    sx={{
                      height: "34px",
                      backgroundColor: activeFilterCount > 0 ? "#FFC001" : "transparent",
                      borderColor: activeFilterCount > 0 ? "#FFC001" : "#2a2a2a",
                      color: activeFilterCount > 0 ? "#000" : "#ccc",
                      fontWeight: 700, fontSize: "13px", textTransform: "none", borderRadius: "8px", px: 1.8,
                      "&:hover": { backgroundColor: "#FFC001", borderColor: "#FFC001", color: "#000" },
                    }}
                  >
                    Filter
                  </Button>
                </Badge>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* ── Listings ── */}
        <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 }, pb: 6 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <CircularProgress sx={{ color: "#FFC001" }} />
            </Box>
          ) : sortedListings.length === 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 10, gap: 2 }}>
              <PetsIcon sx={{ color: "#333", fontSize: 56 }} />
              <Typography sx={{ color: "#555", fontSize: "15px" }}>No listings found</Typography>
              {activeFilterCount > 0 && (
                <Button onClick={() => { setFilter([]); navigate("/listings"); }}
                  sx={{ color: "#FFC001", border: "1px solid rgba(255,192,1,0.3)", borderRadius: "8px", px: 3, py: 0.8, textTransform: "none", fontSize: "13px", "&:hover": { backgroundColor: "rgba(255,192,1,0.08)" } }}>
                  Clear filters
                </Button>
              )}
            </Box>
          ) : view === "Grid" ? (
            <CardListing listData={sortedListings} classes={classes} view="listing tab" />
          ) : (
            <ListView listData={sortedListings} classes={classes} />
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
              <Pagination
                count={totalPages}
                page={pageNumber + 1}
                onChange={(_, val) => { setPageNumber(val - 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                variant="outlined"
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": { color: "#777", borderColor: "#333" },
                  "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: "#FFC001", color: "#000", fontWeight: 700, borderColor: "#FFC001" },
                  "& .MuiPaginationItem-root:hover": { borderColor: "#FFC001", color: "#FFC001" },
                }}
              />
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

const mapStateToProps = ({ Categories, Listings, Auth }) => ({
  classes: Categories.classes,
  productListings: Listings.listings,
  loading: Listings.loading,
  totalListing: Listings.totalListing,
  jwtToken: Auth.jwtToken,
  error: Listings.errorListings,
});

const mapDispatchToProps = (dispatch) => ({
  loadListings: (args) => dispatch(Actions.thunkLoadListing(args)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductListing);
